import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { RichTextMarkdownEditor } from "design-system/molecules/RichTextMarkdownEditor"
import { Select, SelectContent, SelectItem, SelectTrigger } from "design-system/molecules/Select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { FormItem, FormLabel } from "design-system/organisms/Form"
import { whatsappLanguages } from "lopermut"
import { Star, X } from "lucide-react"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { EmailCampaignChannelIntegrationSchema } from "schema"
import { z } from "zod"
import { getCampaignTriggerEmail, replaceCampaignTriggerEmail } from "./campaignTriggerEmail"

type Props = { isDisabled: boolean; type: "gmail" | "outlook" }
type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>

const variables = [
  "conversation.id",
  "conversation.customer.name",
  "conversation.customer.firstName",
  "conversation.customer.lastName",
  "conversation.customer.email",
  "conversation.customer.locale",
  "customer.name",
  "customer.firstName",
  "customer.lastName",
  "customer.email",
  "customer.locale",
].map((value) => ({ value, label: value }))

const variableData = {
  conversation: {
    id: "conversation id",
    customer: {
      name: "customer name",
      firstName: "customer first name",
      lastName: "customer last name",
      email: "customer email",
      locale: "customer locale",
    },
  },
  customer: {
    name: "customer name",
    firstName: "customer first name",
    lastName: "customer last name",
    email: "customer email",
    locale: "customer locale",
  },
}

const languageMap = new Map(whatsappLanguages.map((language) => [language.value, language.label]))

function createEmailIntegration(
  type: Props["type"],
): z.infer<typeof EmailCampaignChannelIntegrationSchema> {
  switch (type) {
    case "gmail":
      return { type, gmailIntegrationIds: [] }
    case "outlook":
      return { type, outlookIntegrationIds: [] }
  }
}

export function OpeningEmailForm({ isDisabled, type }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const [activeLocale, setActiveLocale] = useState<string>()
  const flow = form.watch("flow")
  const integration = form.watch("triggerIntegration")
  const defaultLanguage = form.watch("defaultLanguage")
  const channelIntegration = integration ?? createEmailIntegration(type)
  const emailIntegration = getCampaignTriggerEmail(channelIntegration)
  const openingEmail = emailIntegration?.type === type ? (emailIntegration.openingEmail ?? {}) : {}
  const openingEmailLocaleOrder =
    emailIntegration?.type === type ? (emailIntegration.openingEmailLocaleOrder ?? []) : []
  const addedLocales = [
    ...openingEmailLocaleOrder.filter((locale) => openingEmail[locale]),
    ...Object.keys(openingEmail).filter((locale) => !openingEmailLocaleOrder.includes(locale)),
  ]
  const locales =
    defaultLanguage && openingEmail[defaultLanguage]
      ? [defaultLanguage, ...addedLocales.filter((locale) => locale !== defaultLanguage)]
      : addedLocales
  const selectedLocale = activeLocale && openingEmail[activeLocale] ? activeLocale : locales[0]
  const translations =
    type === "gmail"
      ? "campaigns.trigger.metadata.emailInbox.openingEmail"
      : "campaigns.trigger.metadata.outlookInbox.openingEmail"

  if (flow !== "Outbound") return null

  const update = (next: typeof openingEmail, nextLocaleOrder = addedLocales) => {
    const nextEmailIntegration = {
      ...(emailIntegration?.type === type ? emailIntegration : createEmailIntegration(type)),
      openingEmail: next,
      openingEmailLocaleOrder: nextLocaleOrder,
    }

    form.setValue(
      "triggerIntegration",
      replaceCampaignTriggerEmail(channelIntegration, nextEmailIntegration),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const addLocale = (locale: string) => {
    update({ ...openingEmail, [locale]: { subject: "", body: "" } }, [...addedLocales, locale])
    setActiveLocale(locale)
    if (!defaultLanguage) form.setValue("defaultLanguage", locale, { shouldDirty: true })
  }

  const removeLocale = (locale: string) => {
    if (locale === defaultLanguage) return
    const remaining = { ...openingEmail }
    delete remaining[locale]
    update(
      remaining,
      addedLocales.filter((item) => item !== locale),
    )
    setActiveLocale(Object.keys(remaining)[0])
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-medium">{t(`${translations}.title`)}</h3>
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-muted-foreground">{t(`${translations}.description`)}</p>
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="ghost" size="sm" disabled={isDisabled}>
                {t(`${translations}.variables`)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[560px] max-w-[calc(100vw-2rem)]">
              <MarkdownRenderer className="max-h-96 overflow-auto rounded-md border bg-muted">
                {`\`\`\`json\n${JSON.stringify(variableData, null, 2)}\n\`\`\``}
              </MarkdownRenderer>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <Tabs value={selectedLocale} onValueChange={setActiveLocale} className="gap-0">
        <TabsList className="h-auto flex-wrap gap-1 bg-background p-0">
          {locales.map((locale) => {
            const canRemove = locale !== defaultLanguage && !isDisabled

            return (
              <div key={locale} className="relative">
                <TabsTrigger
                  value={locale}
                  className={canRemove ? "h-auto gap-2 px-4 py-2 pr-8" : "h-auto gap-2 px-4 py-2"}
                >
                  {locale === defaultLanguage && <Star className="size-4 fill-current" />}
                  {languageMap.get(locale) ?? locale}
                </TabsTrigger>
                {canRemove && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-1/2 size-7 -translate-y-1/2"
                    onClick={() => removeLocale(locale)}
                  >
                    <X />
                  </Button>
                )}
              </div>
            )
          })}
          <Select onValueChange={addLocale} disabled={isDisabled}>
            <SelectTrigger className="h-auto w-fit border-0 px-4 py-2 shadow-none">
              {t(`${translations}.addLanguage`)}
            </SelectTrigger>
            <SelectContent>
              {whatsappLanguages
                .filter((language) => !openingEmail[language.value])
                .map((language) => (
                  <SelectItem key={language.value} value={language.value}>
                    {language.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </TabsList>
        {locales.map((locale) => {
          const email = openingEmail[locale]
          return (
            <TabsContent key={locale} value={locale} className="mt-3 flex flex-col gap-3">
              <label className="flex w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground mb-3">
                <Checkbox
                  checked={locale === defaultLanguage}
                  disabled={isDisabled}
                  onCheckedChange={(checked) => {
                    if (checked) form.setValue("defaultLanguage", locale, { shouldDirty: true })
                  }}
                />
                {t(`${translations}.defaultLanguage`)}
              </label>
              <FormItem>
                <FormLabel>{t(`${translations}.subject`)}</FormLabel>
                <VaultVariableAutocomplete
                  variables={variables}
                  disabled={isDisabled}
                  className="w-full"
                >
                  <Input
                    value={email?.subject ?? ""}
                    disabled={isDisabled}
                    onChange={(event) =>
                      update({
                        ...openingEmail,
                        [locale]: { ...email, subject: event.target.value },
                      })
                    }
                  />
                </VaultVariableAutocomplete>
              </FormItem>
              <FormItem>
                <FormLabel>{t(`${translations}.body`)}</FormLabel>
                <RichTextMarkdownEditor
                  value={email?.body ?? ""}
                  onChange={(body) => update({ ...openingEmail, [locale]: { ...email, body } })}
                  variables={variables}
                  disabled={isDisabled}
                />
              </FormItem>
            </TabsContent>
          )
        })}
      </Tabs>
    </div>
  )
}
